
import React, { useState } from 'react';
import { CONTACT_LINKS } from '../constants';
import { Mail, Github, Linkedin, Send } from 'lucide-react';

const Contact: React.FC = () => {
  const [formState, setFormState] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Create mailto link with form data
    const subject = encodeURIComponent(`Message from ${formState.name}`);
    const body = encodeURIComponent(
      `Name: ${formState.name}\nEmail: ${formState.email}\n\nMessage:\n${formState.message}`
    );
    const mailtoLink = `mailto:${CONTACT_LINKS.email}?subject=${subject}&body=${body}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    setSent(true);
    setTimeout(() => setSent(false), 5000);
  };

  return (
    <div className="pt-32 pb-20 px-6 md:px-20 max-w-7xl mx-auto min-h-screen flex flex-col justify-center">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
        <div>
          <h2 className="text-4xl md:text-6xl font-bold mb-8 text-white">Get in touch.</h2>
          <p className="text-slate-400 mb-12 max-w-md text-lg">
            Whether you have a project in mind or just want to say hi, my inbox is always open.
          </p>

          <div className="space-y-6">
            <a href={`mailto:${CONTACT_LINKS.email}`} className="flex items-center gap-4 text-slate-300 hover:text-cyan-400 transition-colors group">
              <div className="w-12 h-12 rounded-full bg-slate-900 flex items-center justify-center group-hover:bg-cyan-950 transition-colors">
                <Mail size={20} />
              </div>
              <span className="font-medium">{CONTACT_LINKS.email}</span>
            </a>
            <a href={CONTACT_LINKS.github} className="flex items-center gap-4 text-slate-300 hover:text-cyan-400 transition-colors group" target="_blank" rel="noreferrer">
              <div className="w-12 h-12 rounded-full bg-slate-900 flex items-center justify-center group-hover:bg-cyan-950 transition-colors">
                <Github size={20} />
              </div>
              <span className="font-medium">GitHub</span>
            </a>
            <a href={CONTACT_LINKS.linkedin} className="flex items-center gap-4 text-slate-300 hover:text-cyan-400 transition-colors group" target="_blank" rel="noreferrer">
              <div className="w-12 h-12 rounded-full bg-slate-900 flex items-center justify-center group-hover:bg-cyan-950 transition-colors">
                <Linkedin size={20} />
              </div>
              <span className="font-medium">LinkedIn</span>
            </a>
          </div>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 rounded-3xl p-8 md:p-12 relative overflow-hidden">
          <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Name</label>
              <input 
                type="text" 
                required
                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                value={formState.name}
                onChange={e => setFormState({...formState, name: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Email Address</label>
              <input 
                type="email" 
                required
                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                value={formState.email}
                onChange={e => setFormState({...formState, email: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Message</label>
              <textarea 
                required
                rows={4}
                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors resize-none"
                value={formState.message}
                onChange={e => setFormState({...formState, message: e.target.value})}
              ></textarea>
            </div>
            
            <button 
              type="submit"
              disabled={sent}
              className={`w-full py-4 rounded-xl font-bold flex items-center justify-center gap-3 transition-all ${sent ? 'bg-green-500 text-white' : 'bg-cyan-500 hover:bg-cyan-400 text-black'}`}
            >
              {sent ? (
                'Message Sent!'
              ) : (
                <>
                  Send Message
                  <Send size={18} />
                </>
              )}
            </button>
          </form>

          {/* Decorative background glow */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-[100px] -mr-32 -mt-32"></div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
